import { Texture, Scene, OrthographicCamera, PlaneGeometry, ShaderMaterial, Vector2, Mesh } from 'three';
import * as TweenMax from 'gsap/umd/TweenMax';

export default class ThreeScene {
    constructor(width, height) {
        this.width = width;
        this.height = height;

        this.camera = null;
        this.scene = null;
        this.controls = null;

        this.mouseX = 0;
        this.mouseY = 0;

        this.intensityHolder = 0;
        this.alphaHolder = 1.2;
    }

    static testPrecision(gl, precision) {
        if (gl === null) return false;
        const fragmentSource = `precision ${precision} float;\nvoid main(){}`;
        const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER);
        gl.shaderSource(fragmentShader, fragmentSource);
        gl.compileShader(fragmentShader);
        return gl.getShaderParameter(fragmentShader, gl.COMPILE_STATUS);
    }

    init(inputCanvas) {
        if (window.matchMedia('(max-width: 450px)').matches) {
            this.noiseSettings = {
                noiseScale: 9.0,
                mouseScale: 0.15,
                noiseIntensity: 0.025,
            };
        } else {
            this.isSmallScreen = false;
            this.noiseSettings = {
                noiseScale: 7.0,
                mouseScale: 0.1,
                noiseIntensity: 0.015,
            };
        }


        this.inputCanvas = inputCanvas;
        this.texture = new Texture(this.inputCanvas);

        this.scene = new Scene();

        this.camera = new OrthographicCamera(this.width / -2, this.width / 2, this.height / 2, this.height / -2, -10000, 10000);
        this.camera.position.z = 2.5;


        this.geometry = new PlaneGeometry(this.width, this.height);

        this.texture.needsUpdate = true;

        const canvas = document.createElement('canvas');
        const fragShader = ThreeScene.testPrecision(canvas.getContext('webgl'), 'highp') ? require('home/shaders/frag.glsl') : require('home/shaders/frag-medium.glsl');

        // console.log('high?', ThreeScene.testPrecision(canvas.getContext('webgl'), 'highp'));

        this.material = new ShaderMaterial({
            vertexShader: require('home/shaders/vert.glsl'),
            fragmentShader: fragShader,
            uniforms: {
                iGlobalTime: {
                    type: 'f',
                    value: 0.0,
                },
                inputTexture: { type: 't', value: this.texture },
                mouse: {
                    type: 'vec2',
                    value: new Vector2(this.mouseX, this.mouseY),
                },
                move: {
                    type: 'vec2',
                    value: new Vector2(0, 0),
                },
                noiseScale: { type: 'f', value: this.noiseSettings.noiseScale },
                mouseScale: { type: 'f', value: this.noiseSettings.mouseScale },
                noiseIntensity: { type: 'f', value: this.noiseSettings.noiseIntensity },
                transitionIntensity: { type: 'f', value: this.intensityHolder },
                transitionAlpha: { type: 'f', value: this.alphaHolder },
            },
            defines: {
                USE_MAP: '',
            },
        });

        this.material.transparent = true;

        this.mesh = new Mesh(this.geometry, this.material);
        this.scene.add(this.mesh);

    }

    animate(time) {
        this.material.uniforms.iGlobalTime.value = time;
    }

    onWindowResize(width, height) {

        this.width = width;
        this.height = height;

        this.camera.left = this.width / -2;
        this.camera.right = this.width / 2;
        this.camera.top = this.height / 2;
        this.camera.bottom = this.height / -2;

        this.mesh.geometry = new PlaneGeometry(this.width, this.height);

        this.camera.updateProjectionMatrix();
        if (window.matchMedia('(max-width: 450px)').matches) {
            this.noiseSettings = {
                noiseScale: 9.0,
                mouseScale: 0.15,
                noiseIntensity: 0.025,
            };
        } else {
            this.noiseSettings = {
                noiseScale: 7.0,
                mouseScale: 0.1,
                noiseIntensity: 0.015,
            };
        }

        this.material.uniforms.noiseScale.value = this.noiseSettings.noiseScale;
        this.material.uniforms.mouseScale.value = this.noiseSettings.mouseScale;
        this.material.uniforms.noiseIntensity.value = this.noiseSettings.noiseIntensity;

    }

    onMouseMove(mouseX, mouseY) {
        this.mouseX = mouseX / this.width;
        this.mouseY = mouseY / this.height;

        this.material.uniforms.mouse.value = new Vector2(this.mouseX, this.mouseY);
    }

    smoothMove(moveX, moveY) {
        this.moveX = moveX;
        this.moveY = moveY;

        this.material.uniforms.move.value = new Vector2(this.moveX, this.moveY);
    }

    shaderTransition(force, duration, delay) {
        const val = { intensity: this.intensityHolder, alpha: this.alphaHolder };

        TweenMax.to(val, duration / 2, {
            intensity: force,
            delay,
            alpha: -1.2,
        })
            .eventCallback('onUpdate', () => {
                this.material.uniforms.transitionIntensity.value = val.intensity;
                this.material.uniforms.transitionAlpha.value = val.alpha;

                this.intensityHolder = val.intensity;
                this.alphaHolder = val.alpha;
            })
            .eventCallback('onComplete', () => {
                TweenMax.to(val, duration / 2, {
                    intensity: 0,
                }).eventCallback('onUpdate', () => {
                    this.material.uniforms.transitionIntensity.value = val.intensity;
                    this.material.uniforms.transitionAlpha.value = val.alpha;

                    this.intensityHolder = val.intensity;
                    this.alphaHolder = val.alpha;
                });
            });
    }

}
